import {
  Home,
  Package,
  Users,
  Settings,
  BarChart3,
  Layers,
  Truck,
  RotateCcw,
  LucideIcon,
  Warehouse,
  Tag,
  Megaphone,
  Percent,
  MapPin,
  UserCog,
  ClipboardList,
  FileText,
  BookOpen,
  Store,
  Monitor,
  Banknote,
  Gift,
  Palette,
  MessageSquare,
  BookMarked,
  Search,
  CalendarClock,
  CalendarRange,
  Globe,
  CreditCard,
  BellRing,
  ShieldBan,
  ShieldAlert,
  Table,
  Receipt,
  AppWindow,
  FolderOpen,
  Star,
  MessageCircleQuestion,
  PackageOpen,
  PackageSearch,
  ListTree,
  Boxes,
  BadgeCheck,
  Tags,
  SlidersHorizontal,
  ShoppingCart,
  ListOrdered,
  FilePenLine,
  PackageCheck,
  Undo2,
  ReceiptText,
  Sparkles,
  ArrowLeftRight,
  Building2,
} from 'lucide-react';
import { type PermissionString } from '@/lib/constants/permissions';
import { type CutObligation } from '@/lib/hooks/use-cut-obligations';

// =============================================================================
// NAVIGATION CONFIGURATION
// =============================================================================
// Single source of truth for all navigation items across the application.
// Used by: TopNav, MobileNav, Sidebar, More menu drawer
// =============================================================================

export interface NavItem {
  href: string;
  /** Additional route prefixes owned by this destination (for example draft-order detail routes). */
  activePaths?: string[];
  labelKey: string;
  icon: LucideIcon;
  badge?: number | boolean; // true = show count from data, number = static badge
  showInTopNav?: boolean;
  showInMobileBottom?: boolean;
  showInMoreMenu?: boolean;
  topNavOrder?: number;
  mobileBottomOrder?: number;
  moreMenuOrder?: number;
  requiredPermission?: PermissionString;
  /** Feature flag key from TenantRegistry.features (e.g., 'blogEnabled') */
  featureRequired?: string;
  /** Requires operations mode to be 'advanced' after platform-assisted certification. */
  opsRequired?: boolean;
  /**
   * Minimum number of stock locations required before this nav item appears.
   *
   * Use this to hide settings that only make sense when a merchant has multiple
   * warehouses (e.g. allocation strategy picker — a single-warehouse store
   * has no routing to configure). Single-warehouse merchants see no noise.
   */
  minStockLocations?: number;
  /** Show a CUT read/cleanup surface only when this tenant has existing data. */
  obligationRequired?: CutObligation;
}

export interface NavSection {
  titleKey?: string;
  items: NavItem[];
}

// -----------------------------------------------------------------------------
// Unified navigation items
// -----------------------------------------------------------------------------
const navigationItems: NavItem[] = [
  { href: '/', labelKey: 'navigation.home', icon: Home,
    showInTopNav: true, topNavOrder: 0, showInMobileBottom: true, mobileBottomOrder: 0 },
  { href: '/products', labelKey: 'navigation.products', icon: PackageOpen,
    showInTopNav: true, topNavOrder: 1, showInMobileBottom: true, mobileBottomOrder: 2, requiredPermission: 'ReadCatalog' },
  { href: '/orders', labelKey: 'navigation.orders', icon: ShoppingCart,
    showInTopNav: true, topNavOrder: 2, showInMobileBottom: true, mobileBottomOrder: 1, requiredPermission: 'ReadOrder' },
  { href: '/customers', labelKey: 'navigation.customers', icon: Users,
    showInTopNav: true, topNavOrder: 3, showInMobileBottom: true, mobileBottomOrder: 3, requiredPermission: 'ReadCustomer' },
  { href: '/marketing/discounts', labelKey: 'navigation.marketing', icon: Megaphone,
    showInTopNav: true, topNavOrder: 3.5, requiredPermission: 'ReadPromotion' },
  { href: '/sales-channels', labelKey: 'navigation.salesChannels', icon: Store,
    showInMoreMenu: true, moreMenuOrder: 2.7, requiredPermission: 'ReadSalesChannel' },
  { href: '/settings', labelKey: 'navigation.settings', icon: Settings,
    showInMoreMenu: true, moreMenuOrder: 3, requiredPermission: 'ReadSettings' },
  { href: '/finance', labelKey: 'navigation.finance', icon: Banknote,
    showInMoreMenu: true, moreMenuOrder: 1.5, requiredPermission: 'ReadAnalytics' },
  { href: '/reports', labelKey: 'navigation.reports', icon: BarChart3,
    showInMoreMenu: true, moreMenuOrder: 2, requiredPermission: 'ReadAnalytics' },
  { href: '/capabilities', labelKey: 'navigation.capabilities', icon: Sparkles,
    showInMoreMenu: true, moreMenuOrder: 2.4 },
  { href: '/blog', labelKey: 'navigation.blog', icon: BookOpen,
    requiredPermission: 'ReadSettings' },
];

// -----------------------------------------------------------------------------
// Backward-compatible derived exports
// -----------------------------------------------------------------------------
export const mainNavItems: NavItem[] = navigationItems
  .filter((i) => i.showInTopNav)
  .sort((a, b) => (a.topNavOrder ?? 0) - (b.topNavOrder ?? 0));

export const mobileBottomNavItems: NavItem[] = navigationItems
  .filter((i) => i.showInMobileBottom)
  .sort((a, b) => (a.mobileBottomOrder ?? 0) - (b.mobileBottomOrder ?? 0));

export const moreMenuItems: NavItem[] = navigationItems
  .filter((i) => i.showInMoreMenu)
  .sort((a, b) => (a.moreMenuOrder ?? 0) - (b.moreMenuOrder ?? 0));

// -----------------------------------------------------------------------------
// Page-specific Sidebars
// -----------------------------------------------------------------------------
export const ordersSidebar: NavSection[] = [
  {
    titleKey: 'navigation.orders',
    items: [
      { href: '/orders', labelKey: 'navigation.allOrders', icon: ListOrdered, badge: true, requiredPermission: 'ReadOrder' },
      { href: '/orders/drafts', activePaths: ['/orders/draft'], labelKey: 'navigation.draftOrders', icon: FilePenLine, requiredPermission: 'CreateOrder' },
      { href: '/orders/fulfilment', labelKey: 'navigation.fulfillment', icon: PackageCheck, requiredPermission: 'ReadOrder' },
      { href: '/orders/shipments', labelKey: 'navigation.carrierShipments', icon: Truck, requiredPermission: 'ReadShipping' },
      { href: '/orders/returns', labelKey: 'navigation.returns', icon: Undo2, badge: true, requiredPermission: 'ReadReturn' },
      { href: '/orders/invoices', labelKey: 'navigation.invoices', icon: ReceiptText, requiredPermission: 'ReadInvoice' },
    ],
  },
];

function pathMatches(pathname: string, candidate: string): boolean {
  return pathname === candidate || pathname.startsWith(`${candidate}/`);
}

/**
 * Resolve the exact destination owned by a contextual sidebar. The top nav
 * communicates the parent area while this helper communicates the current
 * workflow, so both can be active without competing with one another.
 */
export function isSidebarItemActive(
  pathname: string,
  item: NavItem,
  items: NavItem[],
): boolean {
  const itemMatches = [item.href, ...(item.activePaths ?? [])]
    .filter(candidate => pathMatches(pathname, candidate));

  if (itemMatches.length === 0) return false;

  const longestMatchingPath = items
    .flatMap(candidate => [candidate.href, ...(candidate.activePaths ?? [])])
    .filter(candidate => pathMatches(pathname, candidate))
    .sort((a, b) => b.length - a.length)[0];

  return itemMatches.some(candidate => candidate === longestMatchingPath);
}

export const productsSidebar: NavSection[] = [
  {
    titleKey: 'navigation.catalog',
    items: [
      { href: '/products', labelKey: 'navigation.products', icon: PackageOpen, requiredPermission: 'ReadCatalog' },
      { href: '/categories', labelKey: 'navigation.categories', icon: ListTree, requiredPermission: 'ReadCatalog' },
      { href: '/collections', labelKey: 'navigation.collections', icon: Boxes, requiredPermission: 'ReadCatalog' },
      { href: '/inventory', labelKey: 'navigation.inventory', icon: PackageSearch, requiredPermission: 'ReadCatalog' },
      { href: '/brands', labelKey: 'navigation.brands', icon: BadgeCheck, requiredPermission: 'ReadCatalog' },
      { href: '/catalog/labels', labelKey: 'navigation.labels', icon: Tags, requiredPermission: 'ReadProductLabel' },
      { href: '/product-templates', labelKey: 'navigation.productTemplates', icon: SlidersHorizontal, requiredPermission: 'ReadCatalog' },
    ],
  },
  {
    titleKey: 'navigation.inventoryOperations',
    items: [
      { href: '/inventory/transfers', labelKey: 'navigation.transfers', icon: ArrowLeftRight, requiredPermission: 'ReadInventory', opsRequired: true },
      { href: '/inventory/purchase-orders', labelKey: 'navigation.purchaseOrders', icon: ClipboardList, requiredPermission: 'ReadPurchaseOrder', opsRequired: true },
      { href: '/inventory/purchase-orders/vendors', labelKey: 'navigation.vendors', icon: Building2, requiredPermission: 'ReadPurchaseOrder', opsRequired: true },
    ],
  },
];

export const marketingSidebar: NavSection[] = [
  {
    titleKey: 'navigation.promotions',
    items: [
      { href: '/marketing/discounts', labelKey: 'navigation.discounts', icon: Percent, requiredPermission: 'ReadPromotion' },
      { href: '/marketing/catalog-discounts', labelKey: 'navigation.catalogDiscounts', icon: Tag, requiredPermission: 'ReadCatalog', featureRequired: 'advancedPromotions' },
      { href: '/marketing/cashback', labelKey: 'navigation.cashback', icon: Banknote, requiredPermission: 'ReadPromotion' },
      { href: '/marketing/calendar', labelKey: 'navigation.promotionCalendar', icon: CalendarRange, requiredPermission: 'ReadPromotion' },
    ],
  },
  {
    titleKey: 'navigation.growth',
    items: [
      { href: '/marketing/loyalty', labelKey: 'navigation.loyalty', icon: Gift, requiredPermission: 'ReadLoyalty' },
    ],
  },
  {
    titleKey: 'navigation.visibility',
    items: [
      { href: '/marketing/seo', labelKey: 'navigation.seo', icon: Search, requiredPermission: 'ReadSettings', featureRequired: 'seoTools' },
    ],
  },
  {
    titleKey: 'navigation.content',
    items: [
      { href: '/marketing/announcements', labelKey: 'navigation.announcements', icon: Megaphone, requiredPermission: 'ReadSettings' },
      { href: '/blog', labelKey: 'navigation.blog', icon: BookOpen, requiredPermission: 'ReadSettings' },
    ],
  },
];

export const customersSidebar: NavSection[] = [
  {
    titleKey: 'navigation.customers',
    items: [
      { href: '/customers', labelKey: 'navigation.allCustomers', icon: Users, requiredPermission: 'ReadCustomer' },
      { href: '/customers/groups', labelKey: 'navigation.customerGroups', icon: Users, requiredPermission: 'ReadCustomer' },
      { href: '/customers/reviews', labelKey: 'navigation.reviews', icon: Star, requiredPermission: 'ReadReview', obligationRequired: 'reviews' },
      { href: '/customers/review-requests', labelKey: 'navigation.reviewRequests', icon: Megaphone, requiredPermission: 'ReadReview', obligationRequired: 'reviewRequests' },
      { href: '/customers/questions', labelKey: 'navigation.questions', icon: MessageCircleQuestion, requiredPermission: 'ReadProductQA', obligationRequired: 'questions' },
    ],
  },
];

export const salesChannelsSidebar: NavSection[] = [
  {
    titleKey: 'navigation.salesChannels',
    items: [
      { href: '/sales-channels', labelKey: 'navigation.overview', icon: Store, requiredPermission: 'ReadSalesChannel' },
      { href: '/sales-channels/online-store', labelKey: 'navigation.onlineStore', icon: Monitor, requiredPermission: 'ReadSalesChannel' },
      { href: '/sales-channels/online-store/design', labelKey: 'navigation.storeDesign', icon: Palette, requiredPermission: 'UpdateSettings' },
      { href: '/sales-channels/online-store/product-experience', labelKey: 'navigation.productExperience', icon: Package, requiredPermission: 'UpdateSettings' },
    ],
  },
];

export const financeSidebar: NavSection[] = [
  {
    titleKey: 'finance.title',
    items: [
      { href: '/finance', labelKey: 'finance.overview', icon: Banknote, requiredPermission: 'ReadAnalytics' },
      { href: '/finance/transactions', labelKey: 'finance.transactions', icon: Receipt, requiredPermission: 'ReadAnalytics' },
    ],
  },
];

export const settingsSidebar: NavSection[] = [
  {
    titleKey: 'navigation.settingsSections.myStore',
    items: [
      { href: '/settings/store', labelKey: 'navigation.storeInfo', icon: Store, requiredPermission: 'ReadSettings' },
      { href: '/settings/domains', labelKey: 'navigation.domains', icon: Globe, requiredPermission: 'ReadSettings', featureRequired: 'customDomain' },
      { href: '/settings/content-blocks', labelKey: 'metafieldDefinitions.contentBlocks.navLabel', icon: BookMarked, requiredPermission: 'ReadSettings' },
      { href: '/settings/metafield-definitions', labelKey: 'metafieldDefinitions.navLabel', icon: Table, requiredPermission: 'ManageMetafieldDefinitions' },
      { href: '/settings/billing', labelKey: 'navigation.billing', icon: CreditCard, requiredPermission: 'ReadSettings' },
    ],
  },
  {
    titleKey: 'navigation.settingsSections.sales',
    items: [
      { href: '/settings/payment-methods', labelKey: 'navigation.paymentMethods', icon: Banknote, requiredPermission: 'ReadSettings' },
      { href: '/settings/checkout', labelKey: 'navigation.orderSettings', icon: ClipboardList, requiredPermission: 'ReadSettings' },
      { href: '/settings/order-customizations', labelKey: 'navigation.orderCustomizations', icon: SlidersHorizontal, requiredPermission: 'ReadSettings' },
      { href: '/settings/gift-options', labelKey: 'navigation.giftSettings', icon: Gift, requiredPermission: 'ReadSettings' },
      { href: '/settings/checkout-restrictions', labelKey: 'navigation.checkoutRestrictions', icon: ShieldBan, requiredPermission: 'UpdateSettings' },
      { href: '/settings/checkout-risk', labelKey: 'navigation.checkoutRisk', icon: ShieldAlert, requiredPermission: 'ReadSettings' },
      { href: '/settings/return-policy', labelKey: 'navigation.returnPolicy', icon: RotateCcw, requiredPermission: 'ManageReturnSettings' },
      { href: '/settings/scheduling', labelKey: 'navigation.deliveryScheduling', icon: CalendarClock, requiredPermission: 'ReadScheduling' },
      { href: '/settings/invoices', labelKey: 'navigation.invoiceSettings', icon: FileText, requiredPermission: 'ReadInvoice' },
      { href: '/settings/tax', labelKey: 'navigation.vatSettings', icon: Receipt, requiredPermission: 'ReadSettings' },
    ],
  },
  {
    titleKey: 'navigation.settingsSections.shippingAndWarehouses',
    items: [
      { href: '/settings/shipping', labelKey: 'navigation.shippingSettings', icon: Truck, requiredPermission: 'ReadShipping' },
      { href: '/stock-locations', labelKey: 'navigation.stockLocations', icon: MapPin, requiredPermission: 'UpdateSettings' },
      { href: '/settings/inventory', labelKey: 'navigation.inventorySettings', icon: Warehouse, requiredPermission: 'UpdateSettings', minStockLocations: 2, opsRequired: true },
      { href: '/settings/operations', labelKey: 'navigation.operationsSettings', icon: ClipboardList, requiredPermission: 'UpdateSettings', opsRequired: true },
    ],
  },
  {
    titleKey: 'navigation.settingsSections.teamAndNotifications',
    items: [
      { href: '/settings/staff', labelKey: 'navigation.team', icon: UserCog, requiredPermission: 'ReadAdministrator' },
      { href: '/settings/notifications', labelKey: 'navigation.notifications', icon: BellRing, requiredPermission: 'ReadSettings' },
      { href: '/settings/messaging', labelKey: 'navigation.messaging', icon: MessageSquare, requiredPermission: 'ReadSettings' },
      { href: '/settings/messaging/logs', labelKey: 'messaging.logs.navLabel', icon: Table, requiredPermission: 'ReadSettings' },
    ],
  },
  {
    titleKey: 'navigation.settingsSections.regionalSelling',
    items: [
      { href: '/settings/markets', labelKey: 'navigation.markets', icon: Globe, requiredPermission: 'UpdateSettings' },
      { href: '/settings/languages', labelKey: 'navigation.languagesTranslations', icon: BookOpen, requiredPermission: 'UpdateSettings' },
    ],
  },
  {
    titleKey: 'navigation.settingsSections.content',
    items: [
      { href: '/pages', labelKey: 'navigation.pages', icon: FileText, requiredPermission: 'ReadSettings' },
      { href: '/settings/testimonials', labelKey: 'navigation.testimonials', icon: Star, requiredPermission: 'ReadTestimonial', obligationRequired: 'testimonials' },
    ],
  },
  {
    titleKey: 'navigation.settingsSections.appsAndIntegrations',
    items: [
      { href: '/settings/apps', labelKey: 'navigation.apps', icon: AppWindow, requiredPermission: 'ReadSettings', obligationRequired: 'appInstallations' },
      { href: '/settings/data-migration', labelKey: 'navigation.dataMigration', icon: FolderOpen, requiredPermission: 'ReadSettings', obligationRequired: 'dataMigrationJobs' },
    ],
  },
];

const topNavMenuItems: Record<string, NavItem[]> = {
  '/products': productsSidebar.flatMap(section => section.items),
  '/orders': ordersSidebar.flatMap(section => section.items),
  '/customers': customersSidebar
    .flatMap(section => section.items)
    .filter(item => !item.obligationRequired),
  '/marketing/discounts': marketingSidebar
    .flatMap(section => section.items),
};

/** Direct jumps exposed beside a primary header destination. */
export function getTopNavMenuItems(href: string): NavItem[] {
  return topNavMenuItems[href] ?? [];
}

const productsNavPaths = ['/products', '/categories', '/collections', '/inventory', '/brands', '/catalog/labels', '/product-templates', '/product-properties', '/attributes', '/filters', '/facets'];
const settingsNavPaths = ['/settings', '/stock-locations'];

// -----------------------------------------------------------------------------
// Helper function to get sidebar by path
// -----------------------------------------------------------------------------
export function getSidebarForPath(pathname: string): NavSection[] | undefined {
  if (pathname.startsWith('/orders')) return ordersSidebar;
  if (pathname.startsWith('/products')) return productsSidebar;
  if (pathname.startsWith('/categories')) return productsSidebar;
  if (pathname.startsWith('/collections')) return productsSidebar;
  if (pathname.startsWith('/inventory')) return productsSidebar;
  if (pathname.startsWith('/facets')) return productsSidebar;
  if (pathname.startsWith('/brands')) return productsSidebar;
  if (pathname.startsWith('/catalog/labels')) return productsSidebar;
  if (pathname.startsWith('/product-templates')) return productsSidebar;
  if (pathname.startsWith('/product-properties')) return productsSidebar;
  if (pathname.startsWith('/attributes')) return productsSidebar;
  if (pathname.startsWith('/filters')) return productsSidebar;
  if (pathname.startsWith('/gift-cards')) return marketingSidebar;
  // CMS Pages — under Settings sidebar
  if (pathname.startsWith('/pages')) return settingsSidebar;
  // Blog — under Marketing sidebar
  if (pathname.startsWith('/blog')) return marketingSidebar;
  // Stock Locations are accessible from Products sidebar
  if (pathname.startsWith('/customers')) return customersSidebar;
  if (pathname.startsWith('/marketing')) return marketingSidebar;
  if (pathname.startsWith('/sales-channels')) return salesChannelsSidebar;
  if (pathname.startsWith('/finance')) return financeSidebar;
  if (pathname.startsWith('/stock-locations')) return settingsSidebar;
  if (pathname.startsWith('/settings')) return settingsSidebar;
  return undefined;
}

// -----------------------------------------------------------------------------
// Helper function to highlight top-level nav items
// -----------------------------------------------------------------------------
export function isMainNavActive(pathname: string, href: string): boolean {
  if (href === '/') return pathname === '/';
  if (href === '/products') {
    return productsNavPaths.some(
      (path) => pathname === path || pathname.startsWith(`${path}/`)
    );
  }
  if (href === '/settings') {
    return settingsNavPaths.some(
      (path) => pathname === path || pathname.startsWith(`${path}/`)
    ) || pathname.startsWith('/pages');
  }
  if (href === '/marketing/discounts') {
    return pathname === '/marketing'
      || pathname.startsWith('/marketing/')
      || pathname.startsWith('/blog')
      || pathname.startsWith('/gift-cards');
  }
  if (href === '/finance') {
    return pathname === '/finance' || pathname.startsWith('/finance/');
  }
  if (href === '/sales-channels') {
    return pathname === '/sales-channels' || pathname.startsWith('/sales-channels/');
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}
